import React, { useState } from 'react';
import {
    Table, Button, Space, Typography, Switch, InputNumber, Modal, Form,
    Input, Alert, message, Tag
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { MOCK_AD_PLATFORMS } from '../../utils/adPlatforms';

const { Title, Text } = Typography;

// 官网统一以 https:// 前缀存储，表单内只维护前缀后面的地址
const stripProtocol = (url) => (url || '').replace(/^https?:\/\//i, '');
const withHttps = (host) => `https://${(host || '').replace(/^https?:\/\//i, '')}`;

// ══════════════════════════════════════════════
//  广告平台配置（运营管理 → 广告投放 → 广告平台配置）
//  一级 = code（后台自定义，全局唯一，创建后不可改）
//  二级 = 别名（App 上报原始名称，仅名称，全局唯一）
//  仅平台运营可维护，企业侧不展示；仅支持启用/停用，不提供删除
// ══════════════════════════════════════════════

const AdPlatformManage = () => {
    const [platforms, setPlatforms] = useState(() => JSON.parse(JSON.stringify(MOCK_AD_PLATFORMS)));
    const [expandedOwner, setExpandedOwner] = useState(null);

    // 一级平台新增/编辑
    const [primaryVisible, setPrimaryVisible] = useState(false);
    const [editingPrimary, setEditingPrimary] = useState(null);
    const [primaryForm] = Form.useForm();

    // 二级别名新增/编辑
    const [aliasVisible, setAliasVisible] = useState(false);
    const [aliasOwner, setAliasOwner] = useState(null);
    const [editingAlias, setEditingAlias] = useState(null);
    const [aliasForm] = Form.useForm();

    const sortPlatforms = (list) => [...list].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

    // ── 一级：新增/编辑 ──
    const openAddPrimary = () => {
        setEditingPrimary(null);
        primaryForm.resetFields();
        primaryForm.setFieldsValue({ sort: platforms.length + 1 });
        setPrimaryVisible(true);
    };
    const openEditPrimary = (record) => {
        setEditingPrimary(record);
        primaryForm.setFieldsValue({ code: record.code, website: stripProtocol(record.website), sort: record.sort });
        setPrimaryVisible(true);
    };
    const savePrimary = async () => {
        try {
            const v = await primaryForm.validateFields();
            if (editingPrimary) {
                if (platforms.some((p) => p.code === v.code && p.code !== editingPrimary.code)) {
                    message.error('一级 code 已存在（全局唯一）');
                    return;
                }
                setPlatforms((prev) => sortPlatforms(prev.map((p) => (
                    p.code === editingPrimary.code ? { ...p, website: withHttps(v.website), sort: v.sort } : p
                ))));
                message.success('已更新');
            } else {
                if (platforms.some((p) => p.code === v.code)) {
                    message.error('一级 code 已存在（全局唯一）');
                    return;
                }
                setPlatforms((prev) => sortPlatforms([
                    ...prev,
                    { code: v.code, website: withHttps(v.website), status: 1, sort: v.sort, aliases: [] },
                ]));
                message.success('已新增，默认启用');
            }
            setPrimaryVisible(false);
        } catch (e) { /* 校验失败 */ }
    };

    // ── 一级：启用/停用 ──
    const togglePrimary = (record) => {
        const next = record.status === 1 ? 0 : 1;
        Modal.confirm({
            title: next === 1 ? '确认启用该平台？' : '确认停用该平台？',
            content: next === 1
                ? `启用后「${record.code}」将出现在所有广告平台下拉中。`
                : `停用后「${record.code}」及全部别名不再出现在下拉中；存量配置保留并标注「平台已停用」。`,
            okText: next === 1 ? '启用' : '停用',
            cancelText: '取消',
            onOk: () => {
                setPlatforms((prev) => prev.map((p) => (p.code === record.code ? { ...p, status: next } : p)));
                message.success(next === 1 ? '已启用' : '已停用');
            },
        });
    };

    const updatePrimarySort = (record, sort) => {
        setPlatforms((prev) => sortPlatforms(prev.map((p) => (p.code === record.code ? { ...p, sort } : p))));
    };

    // ── 二级别名 ──
    const openAddAlias = (owner) => {
        setAliasOwner(owner);
        setEditingAlias(null);
        aliasForm.resetFields();
        aliasForm.setFieldsValue({ sort: (owner.aliases?.length || 0) + 1 });
        setAliasVisible(true);
    };
    const openEditAlias = (owner, alias) => {
        setAliasOwner(owner);
        setEditingAlias(alias);
        aliasForm.setFieldsValue({ name: alias.name, sort: alias.sort });
        setAliasVisible(true);
    };
    const saveAlias = async () => {
        try {
            const v = await aliasForm.validateFields();
            const name = v.name.trim();
            const duplicated = platforms.some((p) => (p.aliases || []).some(
                (a) => a.name === name && !(editingAlias && a.id === editingAlias.id)
            ));
            if (duplicated) {
                message.error('别名已存在（全局唯一）');
                return;
            }
            const sortAliases = (list) => [...list].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
            setPlatforms((prev) => prev.map((p) => {
                if (p.code !== aliasOwner.code) return p;
                if (editingAlias) {
                    return { ...p, aliases: sortAliases((p.aliases || []).map((a) => (
                        a.id === editingAlias.id ? { ...a, name, sort: v.sort } : a
                    ))) };
                }
                const id = `al_${p.code}_${Date.now()}`;
                return { ...p, aliases: sortAliases([...(p.aliases || []), { id, name, status: 1, sort: v.sort }]) };
            }));
            message.success(editingAlias ? '已更新' : '已新增别名');
            setAliasVisible(false);
        } catch (e) { /* 校验失败 */ }
    };

    const updateAliasSort = (owner, alias, sort) => {
        setPlatforms((prev) => prev.map((p) => {
            if (p.code !== owner.code) return p;
            const next = (p.aliases || []).map((a) => (a.id === alias.id ? { ...a, sort } : a));
            return { ...p, aliases: [...next].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)) };
        }));
    };

    const columns = [
        {
            title: '一级 code', dataIndex: 'code', key: 'code', width: 200,
            render: (t, r) => (
                <Space size={8}>
                    <Text strong style={{ fontFamily: 'monospace' }}>{t}</Text>
                    {r.status !== 1 && <Tag style={{ border: 'none' }}>已停用</Tag>}
                </Space>
            ),
        },
        {
            title: '官网', dataIndex: 'website', key: 'website', width: 240,
            render: (t) => (t
                ? <a href={t} target="_blank" rel="noreferrer">{t}</a>
                : <Text type="secondary">-</Text>),
        },
        {
            title: '别名数量', key: 'aliasCount', width: 120, align: 'center',
            render: (_, r) => <Text>{r.aliases?.length || 0} 个</Text>,
        },
        {
            title: '状态', key: 'status', width: 120, align: 'center',
            render: (_, r) => (
                <Switch
                    checked={r.status === 1}
                    checkedChildren="启用"
                    unCheckedChildren="停用"
                    onChange={() => togglePrimary(r)}
                />
            ),
        },
        {
            title: '排序', key: 'sort', width: 120, align: 'center',
            render: (_, r) => (
                <InputNumber
                    min={1} precision={0} size="small" style={{ width: 80 }}
                    value={r.sort}
                    onChange={(val) => updatePrimarySort(r, val ?? r.sort)}
                />
            ),
        },
        {
            title: '操作', key: 'action', width: 200, fixed: 'right',
            render: (_, r) => (
                <Space size={0}>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditPrimary(r)}>编辑</Button>
                    <Button type="link" size="small" icon={<PlusOutlined />} onClick={() => openAddAlias(r)}>添加别名</Button>
                </Space>
            ),
        },
    ];

    const aliasColumns = [
        { title: '别名', dataIndex: 'name', key: 'name', width: 240 },
        {
            title: '状态', key: 'status', width: 120, align: 'center',
            render: (_, a) => (
                <span style={{ color: a.status === 1 ? '#52c41a' : '#999' }}>
                    ● {a.status === 1 ? '启用' : '停用'}
                </span>
            ),
        },
        {
            title: '排序', key: 'sort', width: 120, align: 'center',
            render: (_, a) => (
                <InputNumber
                    min={1} precision={0} size="small" style={{ width: 80 }}
                    value={a.sort}
                    onChange={(val) => updateAliasSort(expandedOwner, a, val ?? a.sort)}
                />
            ),
        },
        {
            title: '操作', key: 'action', width: 100,
            render: (_, a) => (
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditAlias(expandedOwner, a)}>编辑</Button>
            ),
        },
    ];

    // expandedRowRender 需要知道当前归属的一级平台

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={4} style={{ marginBottom: 0 }}>广告平台配置</Title>
                <Button type="primary" icon={<PlusOutlined />} onClick={openAddPrimary}>新增平台</Button>
            </div>

            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="统一维护 App 上报的广告平台名称"
                description={
                    <span style={{ fontSize: 13 }}>
                        一级 <Text code>code</Text> 为后台自定义标识（全局唯一，创建后不可改）；二级为别名，是 App 上报的原始名称（仅名称、全局唯一），用于将上报值归一到一个平台。
                        下游所有「广告平台」下拉只读取启用的一级 code。仅平台运营可维护，企业侧不展示；平台仅支持启用/停用，不支持删除。
                    </span>
                }
            />

            <Table
                rowKey="code"
                columns={columns}
                dataSource={platforms}
                pagination={false}
                scroll={{ x: 1000 }}
                expandable={{
                    expandedRowRender: (record) => (
                        <Table
                            rowKey="id"
                            size="small"
                            columns={aliasColumns}
                            dataSource={record.aliases || []}
                            pagination={false}
                            locale={{ emptyText: '暂无别名，点击上方「添加别名」新增' }}
                        />
                    ),
                    onExpand: (expanded, record) => setExpandedOwner(expanded ? record : null),
                }}
            />

            {/* 一级平台新增/编辑 */}
            <Modal
                title={editingPrimary ? '编辑广告平台' : '新增广告平台'}
                open={primaryVisible}
                onOk={savePrimary}
                onCancel={() => setPrimaryVisible(false)}
                okText="保存"
                cancelText="取消"
                destroyOnClose
                width={460}
            >
                <Form form={primaryForm} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item
                        name="code"
                        label="一级 code"
                        rules={[{ required: true, message: '请输入一级 code' }]}
                        extra={editingPrimary ? '创建后不可修改' : '后台自定义标识，全局唯一，如 max / taku / fengchuan / AdMob'}
                    >
                        <Input placeholder="请输入一级 code" disabled={!!editingPrimary} maxLength={30} />
                    </Form.Item>
                    <Form.Item
                        name="website"
                        label="官网"
                        rules={[
                            { required: true, message: '请输入官网地址' },
                            {
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    if (value.includes('://') || /\s/.test(value)) {
                                        return Promise.reject(new Error('请勿重复输入 https://，仅填写后面的地址'));
                                    }
                                    if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/\S*)?$/.test(value)) {
                                        return Promise.reject(new Error('请输入正确的域名，如 www.applovin.com'));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                        extra="统一使用 https://，仅填写后面的地址，如 www.applovin.com"
                    >
                        <Input placeholder="www.example.com" addonBefore="https://" maxLength={200} />
                    </Form.Item>
                    <Form.Item name="sort" label="排序" rules={[{ required: true, message: '请输入排序' }]}>
                        <InputNumber min={1} precision={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 二级别名新增/编辑 */}
            <Modal
                title={`${editingAlias ? '编辑' : '新增'}别名 - ${aliasOwner?.code || ''}`}
                open={aliasVisible}
                onOk={saveAlias}
                onCancel={() => setAliasVisible(false)}
                okText="保存"
                cancelText="取消"
                destroyOnClose
                width={460}
            >
                <Form form={aliasForm} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item
                        name="name"
                        label="别名"
                        rules={[{ required: true, message: '请输入别名' }]}
                        extra="App 上报的原始名称，全局唯一，如 applovin / AppLovin / fc"
                    >
                        <Input placeholder="请输入别名" maxLength={50} />
                    </Form.Item>
                    <Form.Item name="sort" label="排序" rules={[{ required: true, message: '请输入排序' }]}>
                        <InputNumber min={1} precision={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default AdPlatformManage;
